import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import clsx from "clsx";
import { useFirestoreTable } from "../../utils/useFirestoreTable";

function getArrayPages(
  maxPages: number,
  page: number,
  isMobile: boolean = false
) {
  const maxDisplayPages = isMobile ? 4 : 6;
  const halfDisplay = Math.floor(maxDisplayPages / 2);

  if (maxPages > maxDisplayPages) {
    if (page <= halfDisplay) {
      return [
        ...Array.from({ length: halfDisplay }, (_, i) => i + 1),
        "...",
        maxPages,
      ];
    }
    if (page > maxPages - halfDisplay) {
      return [
        1,
        "...",
        ...Array.from(
          { length: halfDisplay },
          (_, i) => maxPages - (halfDisplay - 1) + i
        ),
      ];
    }

    if (isMobile) {
      return [1, "...", page, "...", maxPages];
    }

    return [1, "...", page - 1, page, page + 1, "...", maxPages];
  }

  return Array.from({ length: maxPages }, (_, i) => i + 1);
}

interface ListPaginationProps {
  pagination?: number[];
}

const CURSOR_ONLY_COLLECTIONS = new Set(["orders", "messages", "orderItems"]);

const ListPagination: FC<ListPaginationProps> = ({ pagination }) => {
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 900px)" });
  const { t } = useTranslation();
  const {
    data,
    TableState,
    updatePage,
    updateTableState,
    isLoading,
    isFetching,
    refreshData,
  } = useFirestoreTable();
  const allCount = data?.allCount || 0;
  const page = TableState.currentPage;
  console.log(allCount, "allCount");
  const sanitizeAllcount =
    allCount < 0
      ? 0
      : parseInt(
          allCount
            .toString()
            .replaceAll(",", "")
            .replaceAll(".", "")
            .replaceAll(" ", "")
        );
  const maxPages = Math.ceil(sanitizeAllcount / TableState.limit);
  const arrayPages = getArrayPages(maxPages, page, isTabletOrMobile);
  let paginationList = pagination || [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const usesCursorOnlyPagination =
    CURSOR_ONLY_COLLECTIONS.has(TableState.collection) ||
    TableState.collectionGroup === true;
  const canMoveToNextPage = page < maxPages;

  return (
    <div
      className={clsx("pt-2 pb-0 px-4 d-flex align-items-center justify-content-between flex-wrap gap-2 w-100", {
        "pb-6": isTabletOrMobile,
      })}
      style={{ minWidth: 0, overflow: "hidden" }}
    >
      <div
        className={clsx({
          "d-flex align-items-center justify-content-center justify-content-md-start":
            !isTabletOrMobile,
        })}
        style={{ flex: "0 0 auto" }}
      >
        <div
          className="dataTables_length flex-left"
          id="kt_customers_table_length"
          data-testid="pagination"
        >
          <label>
            <select
              name="kt_customers_table_length"
              className="form-select form-select-sm form-select-solid"
              value={TableState.limit}
              onChange={(e) => {
                const selectedValue = parseInt(e.target.value);
                updatePage(1); // Reset to first page on limit change

                const currentLimit = TableState.limit;
                const step = pagination ? pagination[0] : 10; // Interval untuk perubahan limit
                const direction = selectedValue > currentLimit ? 1 : -1; // Tentukan arah (naik atau turun)

                let steps = [];
                for (
                  let i = currentLimit;
                  direction === 1 ? i <= selectedValue : i >= selectedValue;
                  i += step * direction
                ) {
                  steps.push(i);
                }

                // Eksekusi setiap perubahan limit secara bertahap
                steps.forEach((limit, index) => {
                  setTimeout(() => {
                    console.log(limit, "temp limit");
                    updateTableState({ limit });
                  }, 1000); // Interval waktu setiap langkah (500ms)
                });
              }}
            >
              {paginationList?.map((limit) => (
                <option key={limit} value={limit}>
                  {limit}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <div
        className={clsx({
          "d-flex align-items-center justify-content-center justify-content-md-end":
            !isTabletOrMobile,
        })}
        style={{ minWidth: 0, flex: "1 1 auto" }}
      >
        <div id="kt_table_users_paginate" style={{ maxWidth: "100%", overflowX: "auto" }}>
          <ul className="pagination flex-nowrap mb-0">
            <li className={`page-item ${page > 1 ? "" : "disabled"}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => {
                  if (page > 1) updatePage(page - 1);
                }}
              >
                {isTabletOrMobile ? "«" : `« ${t("Pages.Previous")}`}
              </button>
            </li>

            {usesCursorOnlyPagination ? (
              <li className="page-item active">
                <button
                  type="button"
                  className={clsx("page-link", { "me-3": !isTabletOrMobile })}
                >
                  {page}
                </button>
              </li>
            ) : (
              arrayPages.map((i, index) => (
                <li
                  key={`${i}_${index}`}
                  className={clsx("page-item", { active: i === page })}
                >
                  <button
                    type="button"
                    className={clsx(
                      "page-link",
                      { "me-3": !isTabletOrMobile },
                      { disabled: typeof i !== "number" }
                    )}
                    disabled={typeof i !== "number"}
                    onClick={() => {
                      if (typeof i === "number" && i !== page) updatePage(i);
                    }}
                  >
                    {i}
                  </button>
                </li>
              ))
            )}

            <li className={`page-item ${canMoveToNextPage ? "" : "disabled"}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => {
                  if (canMoveToNextPage) updatePage(page + 1);
                }}
              >
                {isTabletOrMobile ? "»" : `${t("Pages.Next")} »`}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ListPagination;
